import { CheckCircle2, CreditCard, Globe, Image as ImageIcon, LayoutTemplate, Menu, Package, Palette, Store, Truck, type LucideIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'
import { AssistantModel } from '@/http/models/assistant/Assistant'
import { Button } from '@/components/ui/button'
import { AssistantSetupStatus } from '@/types/assistant'

import { assistantText, ROUTE_PATHS, SETUP_STEP_TEXT } from './assistantText'
import { useAssistant } from './AssistantProvider'

const ICONS: Record<string, LucideIcon> = {
  store_details: Store, logo: Palette, first_product: Package, payments: CreditCard, delivery: Truck,
  home_page: LayoutTemplate, hero_image: ImageIcon, menu: Menu, domain: Globe,
}

/** The next store setup steps on Home, from the server's checklist; hidden once the required steps are done. */
export const AssistantSetupCards = () => {
  const { language, send } = useAssistant()
  const navigate = useNavigate()
  const t = assistantText(language)
  const [status, setStatus] = useState<AssistantSetupStatus | null>(null)

  useEffect(() => {
    AssistantModel.getSetupStatus().then((res) => {
      if (res.success && res.data) setStatus(res.data.result)
    })
  }, [])

  if (!status || status.done >= status.total) return null
  const next = status.steps.filter((s) => s.status === 'todo' && !s.optional).slice(0, 3)

  return (
    <section className="assistant-fade-in mx-auto w-full max-w-[1088px] space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className={cn('text-heading-md', language === 'bn' && 'font-bn')}>{t.setupTitle}</h2>
          <p className="text-xs text-muted-foreground">{t.setupProgress(status.done, status.total)}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => send(language === 'bn' ? 'আমার স্টোর সেটআপ শেষ করতে সাহায্য করুন' : 'Help me finish setting up my store')}>
          {t.setupAsk}
        </Button>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-fill" aria-hidden>
        <div className="h-full rounded-full bg-violet transition-[width]" style={{ width: `${(status.done / status.total) * 100}%` }} />
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {next.map((step) => {
          const Icon = ICONS[step.id] ?? CheckCircle2
          const [title, hint] = SETUP_STEP_TEXT[step.id]?.[language] ?? [step.label, '']
          return (
            <div key={step.id} className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-card p-4 shadow-card">
              <span className="grid size-10 place-items-center rounded-lg bg-violet-lighter text-violet-dark"><Icon className="size-5" /></span>
              <div className={cn('flex-1 space-y-1', language === 'bn' && 'font-bn')}>
                <p className="text-sm font-semibold">{title}</p>
                {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
              </div>
              <Button variant="outline" size="sm" className="self-start" onClick={() => ROUTE_PATHS[step.route_id] && navigate(ROUTE_PATHS[step.route_id])}>
                {t.setupStart}
              </Button>
            </div>
          )
        })}
      </div>
    </section>
  )
}
